import type { RouteLocationRaw } from "vue-router"

const DIALOGUE_REDIRECT_PATHS = new Set(["/mes-echanges", "/dialogue"])

/**
 * Ajoute le paramètre `source` Dahub à une URL de redirection post-login.
 */
export function appendDialogueSourceToRedirect(
  redirectPath: string,
  sourceOverride?: string | null
): string {
  if (!redirectPath || redirectPath.includes("source=")) {
    return redirectPath
  }

  const source = sourceOverride ?? sessionStorage.getItem("requestedDialogueSource")

  if (!source) {
    return redirectPath
  }

  const [path, search = ""] = redirectPath.split("?")
  const params = new URLSearchParams(search)
  params.set("source", source)

  const query = params.toString()

  return query ? `${path}?${query}` : path
}

/**
 * Transforme une redirection post-login en location Vue Router (path + query).
 */
export function parsePostLoginRedirect(redirectPath: string): RouteLocationRaw {
  const enrichedPath = appendDialogueSourceToRedirect(redirectPath)

  try {
    const url = new URL(enrichedPath, "https://app.uska.fr")
    const query: Record<string, string> = {}

    url.searchParams.forEach((value, key) => {
      query[key] = value
    })

    return {
      path: url.pathname,
      query: Object.keys(query).length > 0 ? query : undefined,
    }
  } catch {
    return { path: enrichedPath }
  }
}

export function isDialogueRedirectPath(redirectPath: string | null | undefined): boolean {
  if (!redirectPath) {
    return false
  }

  const path = redirectPath.split("?")[0]

  return DIALOGUE_REDIRECT_PATHS.has(path) || path.startsWith("/mes-echanges") || path.startsWith("/dialogue")
}

/**
 * Persiste la cible Mes échanges + source campagne Dahub pour le flux magic-link.
 */
export function persistDialogueCampaignRedirect(
  redirectPath: string,
  source?: string | null
): void {
  if (source) {
    sessionStorage.setItem("requestedDialogueSource", source)
  }

  if (!isDialogueRedirectPath(redirectPath)) {
    return
  }

  sessionStorage.setItem(
    "redirectAfterLogin",
    appendDialogueSourceToRedirect(redirectPath, source)
  )
}

/**
 * Résout une redirection post-login en conservant impérativement le paramètre source.
 */
export function resolvePostLoginNavigation(redirectPath: string | null): RouteLocationRaw | null {
  if (!redirectPath || redirectPath === "/authentication") {
    return null
  }

  const source = sessionStorage.getItem("requestedDialogueSource")
  const enrichedPath = isDialogueRedirectPath(redirectPath)
    ? appendDialogueSourceToRedirect(redirectPath, source)
    : redirectPath
  const location = parsePostLoginRedirect(enrichedPath)

  if (!source || !isDialogueRedirectPath(redirectPath)) {
    return location
  }

  if (typeof location !== "object" || location === null || Array.isArray(location)) {
    return location
  }

  return {
    ...location,
    query: {
      ...(location.query ?? {}),
      source,
    },
  }
}
